import React, { FC, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Attachment } from '@eika/file-uploader/lib/file-uploader-types';

import { AppContext } from '../../containers/appContextContainer';
import { ValgtWeb } from '../../generated-types';
import useFeatureToggle from '../../hooks/useFeatureToggle';
import { useLagreLeieinntekter, useLeieinntekter } from '../../hooks/useInntekt';
import { useTranslation } from '../../translations';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import LeieinntekterSkattefrie from './leieinntekterSkattefrie';
import LeieinntekterSkattepliktige from './leieinntekterSkattepliktige';
import Uteleiekontrakt from './uteleiekontrakt';

export type LeieinntekterFormData = {
    skattefrie: number | null;
    skattepliktige: number | null;
    harSkattefrieLeieinntekter: ValgtWeb;
    harSkattepliktigeLeieinntekter: ValgtWeb;
};

interface Props {
    navigateTo: string;
    navigateFrom: string;
}

const Leieinntekter: FC<Props> = ({ navigateTo, navigateFrom }) => {
    const navigate = useNavigate();
    const { leieinntekt } = useTranslation();
    const { soeknadstype } = useContext(AppContext);
    const { data: featureTogglesUtvideDatagrunnlag } = useFeatureToggle();
    const { utvideDatagrunnlag } = featureTogglesUtvideDatagrunnlag || {};
    const { data: leieinntekter } = useLeieinntekter(soeknadstype);
    const { mutate: lagreLeieinntekter, isPending } = useLagreLeieinntekter(() => {
        navigate(navigateTo);
    });
    const methods = useForm<LeieinntekterFormData>({
        defaultValues: {
            harSkattefrieLeieinntekter: ValgtWeb.IKKE_VALGT,
            harSkattepliktigeLeieinntekter: ValgtWeb.IKKE_VALGT,
        },
    });
    const {
        reset,
        handleSubmit,
        getValues,
        formState: { isDirty },
    } = methods;
    const harSkattefrieLeieinntekter = getValues('harSkattefrieLeieinntekter');
    const harSkattepliktigeLeieinntekter = getValues('harSkattepliktigeLeieinntekter');
    const [utleiekontrakt, setUtleiekontrakt] = useState<Attachment[]>([]);

    useEffect(() => {
        if (leieinntekter) {
            reset({
                skattefrie: leieinntekter.skattefrie,
                skattepliktige: leieinntekter.skattepliktige,
                harSkattefrieLeieinntekter: leieinntekter.harSkattefrieLeieinntekter,
                harSkattepliktigeLeieinntekter: leieinntekter.harSkattepliktigeLeieinntekter,
            });
        }
    }, [leieinntekter, reset]);

    const onSubmitLeieinntekter = (data: LeieinntekterFormData) => {
        if (leieinntekter && (isDirty || utleiekontrakt.length > 0)) {
            const formData = new FormData();
            formData.append('soeknadId', leieinntekter.soeknadId);
            formData.append('skattefrie', data.skattefrie?.toString() || 'null');
            formData.append('skattepliktige', data.skattepliktige?.toString() || 'null');
            utleiekontrakt.forEach((file) => {
                formData.append('leiekontrakter', file.file);
            });
            lagreLeieinntekter(formData);
        } else {
            navigate(navigateTo);
        }
    };

    const lastOppUtleiekontrakt = (attachment: Attachment[]) => {
        setUtleiekontrakt(attachment);
    };

    return (
        <PageCard title={leieinntekt.title}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmitLeieinntekter)}>
                    <div className="row row-gap-large">
                        <span className="typography-ingress__light">{leieinntekt.subTitle}</span>
                        <LeieinntekterSkattefrie />
                        <LeieinntekterSkattepliktige />
                        {(harSkattefrieLeieinntekter === ValgtWeb.JA ||
                            harSkattepliktigeLeieinntekter === ValgtWeb.JA) &&
                            utvideDatagrunnlag && (
                                <Uteleiekontrakt
                                    soeknadId={leieinntekter?.soeknadId}
                                    leiekontrakter={leieinntekter?.leiekontrakt}
                                    lastOppUtleiekontrakt={lastOppUtleiekontrakt}
                                />
                            )}
                    </div>
                    <PageFooter
                        soeknadId={leieinntekter?.soeknadId}
                        onForrigeClick={() => navigate(navigateFrom)}
                        showSpinner={isPending}
                    />
                </form>
            </FormProvider>
        </PageCard>
    );
};

export default Leieinntekter;
